import { Component } from "inferno";
import { I18NextService } from "../../../services";
import { Icon } from "../icon";
import { CrossPostButton } from "./create-item-buttons";
import {
  CommentSlimView,
  CommentView,
  Community,
  CommunityModeratorView,
  MyUserInfo,
  NotePerson,
  PersonView,
  PostView,
} from "lemmy-js-client";
import { amAdmin, amTopModExcludeMe, amMod, canAdmin } from "@utils/roles";
import ActionButton from "./action-button";
import classNames from "classnames";
import { Link } from "inferno-router";
import ConfirmationModal from "../modal/confirmation-modal";
import ViewVotesModal from "../modal/view-votes-modal";
import ModActionFormModal, {
  BanUpdateForm,
} from "../modal/mod-action-form-modal";
import {
  BanType,
  CrossPostParams,
  PurgeType,
  ShowBodyType,
  ShowMarkReadType,
} from "@utils/types";
import { getApubName, hostname } from "@utils/helpers";
import { tippyMixin } from "../../mixins/tippy-mixin";
import PersonNoteModal from "../modal/person-note-modal";
import {
  getCommentParentId,
  linkTarget,
  mark_as_read_i18n,
  postIsInteractable,
  userNotLoggedInOrBanned,
} from "@utils/app";
import { canShare } from "@utils/browser";

// TODO there is no reason to try to combine these. It should be completely split into simple PostActionsDropdown, and CommentActionDropdowns, pushing up the simple forms.
interface ContentActionDropdownPropsBase {
  moderators?: CommunityModeratorView[];
  admins: PersonView[];
  community: Community;
  viewSource: boolean;
  myUserInfo: MyUserInfo | undefined;
  onSave: () => void;
  onEdit: () => void;
  onDelete: () => void;
  // TODO These should be pushing up the specific forms, rather than bare reasons.
  onReport: (reason: string) => void;
  onBlockPerson: () => void;
  onBlockCommunity: () => void;
  onRemove: (reason: string) => void;
  onBanFromCommunity: (form: BanUpdateForm) => void;
  onAppointCommunityMod: () => void;
  onTransferCommunity: () => void;
  onBanFromSite: (form: BanUpdateForm) => void;
  onPurgeContent: (reason: string) => void;
  onPurgeUser: (reason: string) => void;
  onAppointAdmin: () => void;
  onPersonNote: (form: NotePerson) => void;
  onLock: (reason: string) => void;
  onWarn: (reason: string) => void;
  onViewSource: () => void;
}

export type ContentCommentProps = {
  type: "comment";
  commentView: CommentView | CommentSlimView;
  showContext: boolean;
  onDistinguish: () => void;
} & ContentActionDropdownPropsBase;

export type ContentPostProps = {
  type: "post";
  postView: PostView;
  crossPostParams: CrossPostParams;
  showBody: ShowBodyType;
  showMarkRead: ShowMarkReadType;
  viewOnly: boolean;
  onFeatureLocal: () => void;
  onFeatureCommunity: () => void;
  onHidePost: () => void;
  onSharePost: () => void;
  onMarkPostAsRead: () => void;
} & ContentActionDropdownPropsBase;

type ContentActionDropdownProps = ContentCommentProps | ContentPostProps;

type DialogType =
  | "BanDialog"
  | "RemoveDialog"
  | "PurgeDialog"
  | "ReportDialog"
  | "TransferCommunityDialog"
  | "AppointModDialog"
  | "AppointAdminDialog"
  | "ViewVotesDialog"
  | "PersonNoteDialog"
  | "LockDialog"
  | "WarnDialog";

type ActionTypeState = {
  banType?: BanType;
  purgeType?: PurgeType;
};

type ShowState = {
  [key in `show${DialogType}`]: boolean;
};

type RenderState = {
  [key in `render${DialogType}`]: boolean;
};

type DropdownState = { dropdownOpenedOnce: boolean };

type ContentActionDropdownState = ActionTypeState &
  ShowState &
  RenderState &
  DropdownState;

@tippyMixin
export default class ContentActionDropdown extends Component<
  ContentActionDropdownProps,
  ContentActionDropdownState
> {
  state: ContentActionDropdownState = {
    showAppointAdminDialog: false,
    showAppointModDialog: false,
    showBanDialog: false,
    showPurgeDialog: false,
    showRemoveDialog: false,
    showReportDialog: false,
    showTransferCommunityDialog: false,
    showViewVotesDialog: false,
    showPersonNoteDialog: false,
    showLockDialog: false,
    showWarnDialog: false,
    renderAppointAdminDialog: false,
    renderAppointModDialog: false,
    renderBanDialog: false,
    renderPurgeDialog: false,
    renderRemoveDialog: false,
    renderReportDialog: false,
    renderTransferCommunityDialog: false,
    renderViewVotesDialog: false,
    renderPersonNoteDialog: false,
    renderLockDialog: false,
    renderWarnDialog: false,
    dropdownOpenedOnce: false,
  };

  render() {
    // Possible enhancement: Priority+ pattern instead of just hard coding which get hidden behind the show more button.
    const { onSave, type, onDelete, onBlockPerson, onEdit, moderators } =
      this.props;
    const {
      id,
      saved_at,
      hidden_at,
      deleted,
      locked,
      removed,
      creator_banned_from_community,
      creator,
      creator_is_admin,
      creator_is_moderator,
      creator_banned,
    } = this.contentInfo;
    const dropdownId =
      type === "post"
        ? `post-actions-dropdown-${id}`
        : `comment-actions-dropdown-${id}`;
    const showToggleAdmin = !creator_banned && creator.local;
    const canAppointCommunityMod =
      (amMod(
        this.props.type === "comment"
          ? this.props.commentView
          : this.props.postView,
      ) ||
        (amAdmin(this.props.myUserInfo) && this.props.community.local)) &&
      !creator_banned_from_community;

    const modHistoryUserTranslation = I18NextService.i18n.t(
      "user_moderation_history",
      { user: creator.name },
    );

    // The link and translation string for the item
    const { modHistoryItemLink, modHistoryItemTranslation } =
      type === "post"
        ? {
            modHistoryItemLink: `/modlog?postId=${id}`,
            modHistoryItemTranslation: I18NextService.i18n.t(
              "post_moderation_history",
            ),
          }
        : {
            modHistoryItemLink: `/modlog?commentId=${id}`,
            modHistoryItemTranslation: I18NextService.i18n.t(
              "comment_moderation_history",
            ),
          };

    const apId =
      type === "post"
        ? this.props.postView.post.ap_id
        : this.props.commentView.comment.ap_id;

    const linkTitle =
      type === "comment" && this.props.showContext
        ? I18NextService.i18n.t("show_context")
        : I18NextService.i18n.t("link");

    const commentId =
      type === "comment"
        ? (this.props.showContext &&
            getCommentParentId(this.props.commentView.comment)) ||
          this.props.commentView.comment.id
        : undefined;

    const link =
      type === "post"
        ? `/post/${this.props.postView.post.id}`
        : `/post/${this.props.commentView.comment.post_id}/${commentId}#comment-${commentId}`;

    return (
      <>
        <div className="dropdown">
          <button
            className="btn btn-sm border-light-subtle btn-animate text-muted"
            data-tippy-content={I18NextService.i18n.t("more")}
            data-bs-toggle="dropdown"
            aria-expanded="false"
            aria-controls={dropdownId}
            aria-label={I18NextService.i18n.t("more")}
            onClick={() => handleDropdownToggleClick(this)}
          >
            <Icon icon="more-vertical" inline />
          </button>

          <ul className="dropdown-menu" id={dropdownId}>
            {this.state.dropdownOpenedOnce && (
              <>
                {/* Links / fedilinks */}
                <li>
                  <Link
                    className="btn btn-light border-light-subtle d-flex align-items-center rounded-0 dropdown-item"
                    to={link}
                    title={linkTitle}
                  >
                    <Icon icon="link" classes="me-2" inline />
                    {linkTitle}
                  </Link>
                </li>
                <li>
                  <a
                    className="btn btn-light border-light-subtle d-flex align-items-center rounded-0 dropdown-item"
                    title={I18NextService.i18n.t("fedilink")}
                    href={apId}
                    target={linkTarget(this.props.myUserInfo)}
                  >
                    <Icon icon="fedilink" classes="me-2" inline />
                    {I18NextService.i18n.t("fedilink")}
                  </a>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                {(type === "comment" ||
                  (type === "post" &&
                    this.props.showBody === "full" &&
                    this.props.postView.post.body)) && (
                  <li>
                    <ActionButton
                      icon="file-text"
                      iconClass={classNames({
                        "text-success": this.props.viewSource,
                      })}
                      label={I18NextService.i18n.t("view_source")}
                      onClick={this.props.onViewSource}
                    />
                  </li>
                )}
                {type === "post" && canShare() && (
                  <ActionButton
                    icon="share"
                    label={I18NextService.i18n.t("share_post")}
                    onClick={this.props.onSharePost}
                  />
                )}
                {this.props.myUserInfo && (
                  <li>
                    <ActionButton
                      onClick={onSave}
                      icon="bookmark"
                      label={I18NextService.i18n.t(
                        saved_at ? "unsave" : "save",
                      )}
                      iconClass={classNames({ "text-warning": saved_at })}
                    />
                  </li>
                )}
                {/* Interactable post items */}
                {type === "post" &&
                  this.props.myUserInfo &&
                  postIsInteractable(
                    this.props.postView,
                    this.props.viewOnly,
                  ) && (
                    <>
                      <li>
                        <CrossPostButton {...this.props.crossPostParams} />
                      </li>
                      {this.props.showMarkRead === "dropdown" && (
                        <li>
                          <ActionButton
                            icon="check"
                            iconClass={classNames({
                              "text-success":
                                this.props.postView.post_actions?.read_at,
                            })}
                            label={mark_as_read_i18n(
                              this.props.postView.post_actions?.read_at !==
                                undefined,
                            )}
                            onClick={this.props.onMarkPostAsRead}
                          />
                        </li>
                      )}
                      <li>
                        <ActionButton
                          icon={hidden_at ? "eye" : "eye-slash"}
                          label={I18NextService.i18n.t(
                            hidden_at ? "unhide_post" : "hide_post",
                          )}
                          onClick={this.props.onHidePost}
                        />
                      </li>
                    </>
                  )}
                {/* Mods / admins can also edit post tags or nsfw. */}
                {((type === "post" && this.canMod) ||
                  (this.amCreator &&
                    !userNotLoggedInOrBanned(this.props.myUserInfo))) && (
                  <>
                    <li>
                      <ActionButton
                        icon="edit"
                        label={I18NextService.i18n.t(
                          this.amCreator ? "edit" : "edit_as_mod",
                        )}
                        noLoading
                        onClick={onEdit}
                      />
                    </li>
                    {this.amCreator && (
                      <li>
                        <ActionButton
                          onClick={onDelete}
                          icon={deleted ? "undo-trash" : "trash"}
                          label={I18NextService.i18n.t(
                            deleted ? "undelete" : "delete",
                          )}
                          iconClass={`text-${deleted ? "success" : "danger"}`}
                        />
                      </li>
                    )}
                  </>
                )}
                {!this.amCreator && this.props.myUserInfo && (
                  <>
                    {type === "comment" && (
                      <li>
                        <Link
                          className="btn btn-light border-light-subtle btn-sm d-flex align-items-center rounded-0 dropdown-item"
                          to={`/create_private_message/${creator.id}`}
                          title={I18NextService.i18n.t("message")}
                          aria-label={I18NextService.i18n.t("message")}
                          data-tippy-content={I18NextService.i18n.t("message")}
                        >
                          <Icon icon="mail" inline classes="me-2" />
                          {I18NextService.i18n.t("message")}
                        </Link>
                      </li>
                    )}

                    <li>
                      <ActionButton
                        icon="flag"
                        label={I18NextService.i18n.t("create_report")}
                        onClick={() => handleToggleReportDialogShow(this)}
                        noLoading
                      />
                    </li>
                    <li>
                      <ActionButton
                        icon="edit"
                        label={I18NextService.i18n.t("create_user_note")}
                        onClick={() => handleTogglePersonNoteShow(this)}
                        noLoading
                      />
                    </li>
                    <li>
                      <ActionButton
                        icon="slash"
                        label={I18NextService.i18n.t("block_user")}
                        onClick={onBlockPerson}
                      />
                    </li>
                    <li>
                      <ActionButton
                        icon="slash"
                        label={I18NextService.i18n.t("block_community")}
                        onClick={this.props.onBlockCommunity}
                      />
                    </li>
                  </>
                )}
                {(amMod(
                  this.props.type === "comment"
                    ? this.props.commentView
                    : this.props.postView,
                ) ||
                  amAdmin(this.props.myUserInfo)) && (
                  <>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <ActionButton
                        onClick={() => handleToggleViewVotesShow(this)}
                        label={I18NextService.i18n.t("view_votes")}
                        icon={"arrow-up"}
                        noLoading
                      />
                    </li>
                    {type === "post" && (
                      <>
                        <li>
                          <ActionButton
                            onClick={this.props.onFeatureCommunity}
                            label={I18NextService.i18n.t(
                              this.props.postView.post.featured_community
                                ? "unfeature_from_community"
                                : "feature_in_community",
                            )}
                            icon={
                              this.props.postView.post.featured_community
                                ? "pin-off"
                                : "pin"
                            }
                          />
                        </li>
                        {amAdmin(this.props.myUserInfo) && (
                          <li>
                            <ActionButton
                              onClick={this.props.onFeatureLocal}
                              label={I18NextService.i18n.t(
                                this.props.postView.post.featured_local
                                  ? "unfeature_from_local"
                                  : "feature_in_local",
                              )}
                              icon={
                                this.props.postView.post.featured_local
                                  ? "pin-off"
                                  : "pin"
                              }
                            />
                          </li>
                        )}
                      </>
                    )}
                  </>
                )}
                {type === "comment" &&
                  this.amCreator &&
                  (this.canModOnSelf || this.canAdminOnSelf) && (
                    <li>
                      <ActionButton
                        onClick={this.props.onDistinguish}
                        icon={
                          this.props.commentView.comment.distinguished
                            ? "shield-off"
                            : "shield"
                        }
                        label={I18NextService.i18n.t(
                          this.props.commentView.comment.distinguished
                            ? "undistinguish"
                            : "distinguish",
                        )}
                      />
                    </li>
                  )}
                <li>
                  <Link
                    className="btn btn-light border-light-subtle btn-sm d-flex align-items-center rounded-0 dropdown-item"
                    to={`/modlog?userId=${creator.id}`}
                    title={modHistoryUserTranslation}
                    aria-label={modHistoryUserTranslation}
                    data-tippy-content={modHistoryUserTranslation}
                  >
                    <Icon icon="history" inline classes="me-2" />
                    {modHistoryUserTranslation}
                  </Link>
                </li>
                <li>
                  <Link
                    className="btn btn-light border-light-subtle btn-sm d-flex align-items-center rounded-0 dropdown-item"
                    to={modHistoryItemLink}
                    title={modHistoryItemTranslation}
                    aria-label={modHistoryItemTranslation}
                    data-tippy-content={modHistoryItemTranslation}
                  >
                    <Icon icon="history" inline classes="me-2" />
                    {modHistoryItemTranslation}
                  </Link>
                </li>
                {(this.canMod || this.canAdmin) && (
                  <>
                    <li>
                      <ActionButton
                        label={
                          removed
                            ? `${I18NextService.i18n.t(
                                type === "post"
                                  ? "restore_post"
                                  : "restore_comment",
                              )}`
                            : I18NextService.i18n.t(
                                type === "post"
                                  ? "remove_post"
                                  : "remove_comment",
                              )
                        }
                        icon={removed ? "restore" : "x"}
                        noLoading
                        onClick={() => handleToggleRemoveShow(this)}
                        iconClass={`text-${removed ? "success" : "danger"}`}
                      />
                    </li>
                    <li>
                      <ActionButton
                        noLoading
                        onClick={() => handleToggleLockShow(this)}
                        label={I18NextService.i18n.t(
                          locked
                            ? type === "post"
                              ? "unlock_post"
                              : "unlock_comment"
                            : type === "post"
                              ? "lock_post"
                              : "lock_comment",
                        )}
                        icon={locked ? "unlock" : "lock"}
                      />
                    </li>
                    <li>
                      <ActionButton
                        noLoading
                        onClick={() => handleToggleWarnShow(this)}
                        label={I18NextService.i18n.t("warn_user")}
                        icon="alert-triangle"
                        iconClass="text-warning"
                      />
                    </li>
                  </>
                )}
                {this.canMod &&
                  (!creator_is_moderator || canAppointCommunityMod) && (
                    <>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      {!creator_is_moderator && (
                        <li>
                          <ActionButton
                            onClick={() =>
                              handleToggleBanFromCommunityShow(this)
                            }
                            label={I18NextService.i18n.t(
                              creator_banned_from_community
                                ? "unban_from_community"
                                : "ban_from_community",
                            )}
                            icon={
                              creator_banned_from_community ? "unban" : "ban"
                            }
                            noLoading
                            iconClass={`text-${
                              creator_banned_from_community
                                ? "success"
                                : "danger"
                            }`}
                          />
                        </li>
                      )}
                      {canAppointCommunityMod && (
                        <li>
                          <ActionButton
                            onClick={() => handleToggleAppointModShow(this)}
                            label={I18NextService.i18n.t(
                              `${
                                creator_is_moderator ? "remove" : "appoint"
                              }_as_mod`,
                            )}
                            icon={creator_is_moderator ? "demote" : "promote"}
                            iconClass={`text-${
                              creator_is_moderator ? "danger" : "success"
                            }`}
                            noLoading
                          />
                        </li>
                      )}
                    </>
                  )}
                {this.props.myUserInfo &&
                  (amTopModExcludeMe(
                    creator.id,
                    moderators,
                    this.props.myUserInfo,
                  ) ||
                    this.canAdmin) &&
                  creator_is_moderator && (
                    <li>
                      <ActionButton
                        label={I18NextService.i18n.t("transfer_community")}
                        onClick={() => handleToggleTransferCommunityShow(this)}
                        icon="transfer"
                        noLoading
                      />
                    </li>
                  )}
                {this.canAdmin && (showToggleAdmin || !creator_is_admin) && (
                  <>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    {!creator_is_admin && (
                      <>
                        <li>
                          <ActionButton
                            label={I18NextService.i18n.t(
                              creator_banned
                                ? "unban_from_site"
                                : "ban_from_site",
                            )}
                            onClick={() => handleToggleBanFromSiteShow(this)}
                            icon={creator_banned ? "unban" : "ban"}
                            iconClass={`text-${
                              creator_banned ? "success" : "danger"
                            }`}
                            noLoading
                          />
                        </li>
                        <li>
                          <ActionButton
                            label={I18NextService.i18n.t("purge_user")}
                            onClick={() => handleTogglePurgePersonShow(this)}
                            icon="purge"
                            noLoading
                            iconClass="text-danger"
                          />
                        </li>
                        <li>
                          <ActionButton
                            label={I18NextService.i18n.t(
                              `purge_${type === "post" ? "post" : "comment"}`,
                            )}
                            onClick={() => handleTogglePurgeContentShow(this)}
                            icon="purge"
                            noLoading
                            iconClass="text-danger"
                          />
                        </li>
                      </>
                    )}
                    {showToggleAdmin && (
                      <li>
                        <ActionButton
                          label={I18NextService.i18n.t(
                            `${creator_is_admin ? "remove" : "appoint"}_as_admin`,
                          )}
                          onClick={() => handleToggleAppointAdminShow(this)}
                          icon={creator_is_admin ? "demote" : "promote"}
                          iconClass={`text-${
                            creator_is_admin ? "danger" : "success"
                          }`}
                          noLoading
                        />
                      </li>
                    )}
                  </>
                )}
              </>
            )}
          </ul>
        </div>
        {this.allDialogs}
      </>
    );
  }

  get allDialogs() {
    const {
      showBanDialog,
      showPurgeDialog,
      showRemoveDialog,
      showReportDialog,
      banType,
      purgeType,
      showTransferCommunityDialog,
      showAppointModDialog,
      showAppointAdminDialog,
      showViewVotesDialog,
      showPersonNoteDialog,
      showLockDialog,
      showWarnDialog,
      renderBanDialog,
      renderPurgeDialog,
      renderRemoveDialog,
      renderReportDialog,
      renderTransferCommunityDialog,
      renderAppointModDialog,
      renderAppointAdminDialog,
      renderViewVotesDialog,
      renderPersonNoteDialog,
      renderLockDialog,
      renderWarnDialog,
    } = this.state;
    const {
      removed,
      locked,
      creator,
      creator_banned_from_community,
      creator_is_admin,
      creator_is_moderator,
      creator_banned,
      id,
      person_actions,
    } = this.contentInfo;
    const {
      onReport,
      onRemove,
      onBanFromCommunity,
      onBanFromSite,
      onPurgeContent,
      onPurgeUser,
      onTransferCommunity,
      onAppointCommunityMod,
      onAppointAdmin,
      onPersonNote,
      onLock,
      onWarn,
      type,
      community,
    } = this.props;

    return (
      <>
        {renderRemoveDialog && (
          <ModActionFormModal
            onSubmit={(reason: string) => {
              handleHideAllDialogs(this);
              onRemove(reason);
            }}
            modActionType={
              type === "comment" ? "remove-comment" : "remove-post"
            }
            isRemoved={removed}
            onCancel={() => handleHideAllDialogs(this)}
            show={showRemoveDialog}
            loading={false}
          />
        )}
        {renderBanDialog && (
          <ModActionFormModal
            onSubmit={(reason: BanUpdateForm) => {
              handleHideAllDialogs(this);
              if (banType === "community") {
                onBanFromCommunity(reason);
              } else {
                onBanFromSite(reason);
              }
            }}
            modActionType={
              banType === "community" ? "community-ban" : "site-ban"
            }
            creator={creator}
            onCancel={() => handleHideAllDialogs(this)}
            isBanned={
              banType === "community"
                ? !!creator_banned_from_community
                : banType === "site"
                  ? creator_banned
                  : false
            }
            community={community}
            show={showBanDialog}
            loading={false}
          />
        )}
        {renderReportDialog && (
          <ModActionFormModal
            onSubmit={(reason: string) => {
              handleHideAllDialogs(this);
              onReport(reason);
            }}
            modActionType={
              type === "comment" ? "report-comment" : "report-post"
            }
            onCancel={() => handleHideAllDialogs(this)}
            show={showReportDialog}
            loading={false}
          />
        )}
        {renderPurgeDialog && (
          <ModActionFormModal
            onSubmit={(reason: string) => {
              handleHideAllDialogs(this);
              if (purgeType === "person") {
                onPurgeUser(reason);
              } else {
                onPurgeContent(reason);
              }
            }}
            modActionType={
              purgeType === "post"
                ? "purge-post"
                : purgeType === "comment"
                  ? "purge-comment"
                  : "purge-person"
            }
            creator={creator}
            onCancel={() => handleHideAllDialogs(this)}
            show={showPurgeDialog}
            loading={false}
          />
        )}
        {renderTransferCommunityDialog && (
          <ConfirmationModal
            show={showTransferCommunityDialog}
            message={I18NextService.i18n.t("transfer_community_are_you_sure", {
              user: getApubName(creator),
              community: getApubName(community),
            })}
            loadingMessage={I18NextService.i18n.t("transferring_community")}
            onNo={() => handleHideAllDialogs(this)}
            onYes={() => {
              handleHideAllDialogs(this);
              onTransferCommunity();
            }}
          />
        )}
        {renderAppointModDialog && (
          <ConfirmationModal
            show={showAppointModDialog}
            message={I18NextService.i18n.t(
              creator_is_moderator
                ? "remove_as_mod_are_you_sure"
                : "appoint_as_mod_are_you_sure",
              {
                user: getApubName(creator),
                community: getApubName(community),
              },
            )}
            loadingMessage={I18NextService.i18n.t(
              creator_is_moderator ? "removing_mod" : "appointing_mod",
            )}
            onNo={() => handleHideAllDialogs(this)}
            onYes={() => {
              handleHideAllDialogs(this);
              onAppointCommunityMod();
            }}
          />
        )}
        {renderAppointAdminDialog && (
          <ConfirmationModal
            show={showAppointAdminDialog}
            message={I18NextService.i18n.t(
              creator_is_admin
                ? "remove_as_admin_are_you_sure"
                : "appoint_as_admin_are_you_sure",
              {
                user: getApubName(creator),
                instance: hostname(creator.ap_id),
              },
            )}
            loadingMessage={I18NextService.i18n.t(
              creator_is_admin ? "removing_admin" : "appointing_admin",
            )}
            onNo={() => handleHideAllDialogs(this)}
            onYes={() => {
              handleHideAllDialogs(this);
              onAppointAdmin();
            }}
          />
        )}
        {renderViewVotesDialog && (
          <ViewVotesModal
            type={type}
            id={id}
            show={showViewVotesDialog}
            onCancel={() => handleHideAllDialogs(this)}
            myUserInfo={this.props.myUserInfo}
          />
        )}
        {renderPersonNoteDialog && (
          <PersonNoteModal
            note={person_actions?.note}
            personId={creator.id}
            show={showPersonNoteDialog}
            onSubmit={form => {
              handleHideAllDialogs(this);
              onPersonNote(form);
            }}
            loading={false}
            onCancel={() => handleHideAllDialogs(this)}
          />
        )}
        {renderLockDialog && (
          <ModActionFormModal
            onSubmit={(reason: string) => {
              handleHideAllDialogs(this);
              onLock(reason);
            }}
            modActionType={type === "post" ? "lock-post" : "lock-comment"}
            onCancel={() => handleHideAllDialogs(this)}
            show={showLockDialog}
            loading={false}
            isLocked={locked}
          />
        )}
        {renderWarnDialog && (
          <ModActionFormModal
            onSubmit={(reason: string) => {
              handleHideAllDialogs(this);
              onWarn(reason);
            }}
            modActionType={type === "post" ? "warn-post" : "warn-comment"}
            onCancel={() => handleHideAllDialogs(this)}
            show={showWarnDialog}
            loading={false}
          />
        )}
      </>
    );
  }

  get contentInfo() {
    if (this.props.type === "post") {
      const {
        post: { id, deleted, locked, removed },
        post_actions: { saved_at, hidden_at } = {},
        creator,
        creator_banned_from_community,
        creator_is_moderator,
        community,
        creator_is_admin,
        creator_banned,
        person_actions,
      } = this.props.postView;

      return {
        id,
        saved_at,
        hidden_at,
        deleted,
        creator,
        locked,
        removed,
        creator_banned_from_community,
        community,
        creator_is_admin,
        creator_is_moderator,
        creator_banned,
        person_actions,
      };
    } else {
      const {
        comment: { id, deleted, removed, locked },
        comment_actions: { saved_at } = {},
        creator,
        creator_banned_from_community,
        creator_is_moderator,
        creator_is_admin,
        creator_banned,
        person_actions,
      } = this.props.commentView;

      return {
        id,
        saved_at,
        deleted,
        creator,
        locked,
        removed,
        creator_banned_from_community,
        creator_is_admin,
        creator_is_moderator,
        creator_banned,
        person_actions,
      };
    }
  }

  get amCreator() {
    const { creator } = this.contentInfo;

    return creator.id === this.props.myUserInfo?.local_user_view.person.id;
  }

  get canMod() {
    const { creator } = this.contentInfo;
    if (canAdmin(creator.id, this.props.admins, this.props.myUserInfo)) {
      return true;
    }
    if (this.props.type === "comment") {
      return this.props.commentView.can_mod;
    }
    return this.props.postView.can_mod;
  }

  get canAdmin() {
    const { creator } = this.contentInfo;
    return canAdmin(creator.id, this.props.admins, this.props.myUserInfo);
  }

  get canModOnSelf() {
    const { creator } = this.contentInfo;
    if (this.props.myUserInfo?.local_user_view.person.id !== creator.id) {
      return false;
    }
    return this.canMod;
  }

  get canAdminOnSelf() {
    const { creator } = this.contentInfo;
    return canAdmin(creator.id, this.props.admins, this.props.myUserInfo, true);
  }
}

function handleDropdownToggleClick(i: ContentActionDropdown) {
  // This only renders the dropdown. Bootstrap handles the show/hide part.
  i.setState({ dropdownOpenedOnce: true });
}

function handleToggleDialogShow(
  i: ContentActionDropdown,
  dialogType: DialogType,
  stateOverride: Partial<ActionTypeState> = {},
) {
  const showKey: keyof ShowState = `show${dialogType}`;
  const renderKey: keyof RenderState = `render${dialogType}`;
  i.setState<keyof ShowState>({
    showBanDialog: false,
    showRemoveDialog: false,
    showPurgeDialog: false,
    showReportDialog: false,
    showTransferCommunityDialog: false,
    showAppointModDialog: false,
    showAppointAdminDialog: false,
    showViewVotesDialog: false,
    showPersonNoteDialog: false,
    showLockDialog: false,
    showWarnDialog: false,
    [showKey]: !i.state[showKey],
    [renderKey]: true, // for fade out just keep rendering after show becomes false
    ...stateOverride,
  });
}

function handleHideAllDialogs(i: ContentActionDropdown) {
  i.setState<keyof ShowState>({
    showBanDialog: false,
    showPurgeDialog: false,
    showRemoveDialog: false,
    showReportDialog: false,
    showAppointAdminDialog: false,
    showAppointModDialog: false,
    showTransferCommunityDialog: false,
    showViewVotesDialog: false,
    showPersonNoteDialog: false,
    showLockDialog: false,
    showWarnDialog: false,
  });
}

function handleToggleReportDialogShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "ReportDialog");
}

function handleToggleRemoveShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "RemoveDialog");
}

function handleToggleBanFromCommunityShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "BanDialog", {
    banType: "community",
  });
}

function handleToggleBanFromSiteShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "BanDialog", {
    banType: "site",
  });
}

function handleTogglePurgePersonShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "PurgeDialog", {
    purgeType: "person",
  });
}

function handleTogglePurgeContentShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "PurgeDialog", {
    purgeType: i.props.type === "post" ? "post" : "comment",
  });
}

function handleToggleTransferCommunityShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "TransferCommunityDialog");
}

function handleToggleAppointModShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "AppointModDialog");
}

function handleToggleAppointAdminShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "AppointAdminDialog");
}

function handleToggleViewVotesShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "ViewVotesDialog");
}

function handleTogglePersonNoteShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "PersonNoteDialog");
}

function handleToggleLockShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "LockDialog");
}

function handleToggleWarnShow(i: ContentActionDropdown) {
  handleToggleDialogShow(i, "WarnDialog");
}
